import React from 'react';
import { cn } from '../utils/cn';

/**
 * Lumo Coaching Feedback Display
 * Shows coaching insights with tone-appropriate styling
 */

const toneBgColors = {
  supportive: 'bg-blue-50 border-blue-200',
  direct: 'bg-orange-50 border-orange-200',
  celebratory: 'bg-green-50 border-green-200',
  analytical: 'bg-purple-50 border-purple-200'
};

const toneTextColors = {
  supportive: 'text-blue-900',
  direct: 'text-orange-900',
  celebratory: 'text-green-900',
  analytical: 'text-purple-900'
};

const toneIcons = {
  supportive: '💡',
  direct: '🎯',
  celebratory: '🎉',
  analytical: '📊'
};

const categoryLabels = {
  tone_correction: 'Tone',
  probe_sharpening: 'Probe Quality',
  detachment_coaching: 'Detachment',
  stack_navigation: 'Stack Handling',
  better_than_yesterday: 'Progress'
};

/**
 * Individual coaching feedback item
 */
export const LumoFeedbackItem = ({ feedback, className }) => {
  const { category, tone, message } = feedback;

  return (
    <div
      className={cn(
        'p-4 rounded-lg border-2 transition-all hover:shadow-md',
        toneBgColors[tone] || 'bg-gray-50 border-gray-200',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">
          {toneIcons[tone] || '💬'}
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                'text-xs font-semibold uppercase tracking-wide',
                toneTextColors[tone] || 'text-gray-700'
              )}
            >
              {categoryLabels[category] || category}
            </span>
            <span className="text-xs text-gray-500 capitalize">
              • {tone}
            </span>
          </div>
          <p
            className={cn(
              'text-sm leading-relaxed',
              toneTextColors[tone] || 'text-gray-900'
            )}
          >
            {message}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Lumo coaching feedback list
 */
export const LumoCoachingFeedback = ({ feedback, className }) => {
  if (!feedback || feedback.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-3', className)}>
      <h3 className="text-lg font-semibold text-gray-900 mb-3">
        Lumo Coaching Insights
      </h3>
      {feedback.map((item, index) => (
        <LumoFeedbackItem key={index} feedback={item} />
      ))}
    </div>
  );
};

/**
 * Lumo score bar (0-1 scale)
 */
export const LumoScoreBar = ({ label, value, className }) => {
  const percentage = Math.round(value * 100);

  // Color based on score
  const getColor = (score) => {
    if (score >= 0.85) return 'bg-green-500';
    if (score >= 0.70) return 'bg-blue-500';
    if (score >= 0.60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getTextColor = (score) => {
    if (score >= 0.85) return 'text-green-700';
    if (score >= 0.70) return 'text-blue-700';
    if (score >= 0.60) return 'text-yellow-700';
    return 'text-red-700';
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className={cn('text-sm font-bold', getTextColor(value))}>
          {percentage}%
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={cn('h-2.5 rounded-full transition-all', getColor(value))}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Lumo score card (all 3 scores + overall)
 */
export const LumoScoreCard = ({ scores, className }) => {
  const { toneConsistency, probeSharpness, detachment, overall } = scores;

  return (
    <div className={cn('bg-white rounded-lg border border-gray-200 p-6', className)}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Lumo Strategy Scores
      </h3>
      <div className="space-y-4">
        <LumoScoreBar label="Tone Consistency" value={toneConsistency} />
        <LumoScoreBar label="Probe Sharpness" value={probeSharpness} />
        <LumoScoreBar label="Detachment" value={detachment} />
        <div className="pt-3 border-t border-gray-200">
          <LumoScoreBar label="Overall Lumo Score" value={overall} />
        </div>
      </div>
    </div>
  );
};

/**
 * Improvement delta badge
 */
export const ImprovementBadge = ({ delta, className }) => {
  if (delta === null || delta === undefined) {
    return null;
  }

  const isPositive = delta > 0;
  const isSignificant = Math.abs(delta) >= 5;

  const getBgColor = () => {
    if (!isSignificant) return 'bg-gray-100 text-gray-700';
    return isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
  };

  const getIcon = () => {
    if (!isSignificant) return '→';
    return isPositive ? '↑' : '↓';
  };

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold',
        getBgColor(),
        className
      )}
    >
      <span className="text-lg">{getIcon()}</span>
      <span>
        {isPositive ? '+' : ''}
        {delta.toFixed(1)}%
      </span>
      <span className="text-xs font-normal opacity-80">vs. last time</span>
    </div>
  );
};

/**
 * Better Than Yesterday tracker
 */
export const BetterThanYesterday = ({ currentScore, previousScore, delta, className }) => {
  const hasImproved = delta > 0;
  const message = hasImproved
    ? "You're getting better. That's the loop."
    : delta < -5
    ? "Let's tighten the probes — you had this before."
    : "Consistent. Keep stacking wins.";

  return (
    <div className={cn('bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6', className)}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Better Than Yesterday
          </h3>
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        <ImprovementBadge delta={delta} />
      </div>
      <div className="flex items-center gap-6">
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-400">{previousScore}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Previous</div>
        </div>
        <div className="text-2xl text-gray-400">→</div>
        <div className="text-center">
          <div className="text-3xl font-bold text-purple-600">{currentScore}</div>
          <div className="text-xs text-purple-600 uppercase tracking-wide font-semibold">Current</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Avoid phrases alert
 */
export const AvoidPhrasesAlert = ({ detectedPhrases, className }) => {
  if (!detectedPhrases || detectedPhrases.length === 0) {
    return null;
  }

  return (
    <div className={cn('bg-red-50 border-2 border-red-200 rounded-lg p-4', className)}>
      <div className="flex items-start gap-3">
        <span className="text-2xl">⚠️</span>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-red-900 mb-2">
            Salesy Language Detected
          </h4>
          <div className="space-y-2">
            {detectedPhrases.map((phrase, index) => (
              <div key={index} className="text-sm">
                <span className="text-red-700 font-mono bg-red-100 px-2 py-0.5 rounded">
                  "{phrase}"
                </span>
                <span className="text-red-600 ml-2">
                  → Try neutral acknowledgment instead
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Full Lumo coaching panel (scores + feedback + improvement)
 */
export const LumoCoachingPanel = ({
  scores,
  feedback,
  currentScore,
  previousScore,
  improvementDelta,
  detectedPhrases,
  className
}) => {
  return (
    <div className={cn('space-y-6', className)}>
      {/* Scores */}
      <LumoScoreCard scores={scores} />

      {/* Better Than Yesterday */}
      {previousScore !== null && improvementDelta !== null && (
        <BetterThanYesterday
          currentScore={currentScore}
          previousScore={previousScore}
          delta={improvementDelta}
        />
      )}

      {/* Avoid Phrases Alert */}
      {detectedPhrases && detectedPhrases.length > 0 && (
        <AvoidPhrasesAlert detectedPhrases={detectedPhrases} />
      )}

      {/* Coaching Feedback */}
      <LumoCoachingFeedback feedback={feedback} />
    </div>
  );
};

export default LumoCoachingPanel;
